// Formatters de subida por template.
//
// Cada template puede tener su propio "formatter": qué ficheros acepta y cómo se
// traducen las columnas del fichero del cliente a las variables del contacto
// ({phone}, {name}, {contractNumber}, …). Se resuelve con `getFormatter(template)`:
//   1. campo `formatter` de la template (si existe en DynamoDB), o
//   2. detección por nombre/flow (preventivo antes que recobros: ambos contienen "caixa").
// Encima, si la template tiene `uploadMapper` (editable desde Templates), sus
// columnas sustituyen a las del código — salvo en formatters `excel` (recobros),
// cuyo Excel lo procesa el backend y no se puede remapear desde aquí.
// Sin formatter ni uploadMapper → genérico (CAMPAIGNS[schema].maps en config/app.js).
//
// `columns` es la fuente de verdad del mapper: se usa para transformar y para
// mostrarlo en la vista de templates y en la subida.

// Normaliza una cabecera para compararla: mayúsculas, sin tildes, sin espacios.
export const normalizeHeader = (h) =>
  String(h || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '')
    .toUpperCase()

export const FORMATTERS = {
  // Recobros CaixaBank: Excel diario procesado en backend (/process-excel) + CSV/JSON
  // genérico. NO tiene mapRow: el CSV sigue por el camino de siempre (schema).
  caixaRecobros: {
    id: 'caixaRecobros',
    label: 'CaixaBank Recobros',
    excel: true,
    accepts: ['xlsx', 'xlsm', 'csv', 'json'],
    description:
      'Excel diario SEGUIMIENTO_RCS_HOY (.xlsx/.xlsm). Hoja ENTRADAS HOY → crea campaña; hoja SEGUIMIENTO_REG_TRA_7_DIAS → cierra flujos con REG_TRA_7D=1. Cabecera en la fila 3. También acepta CSV/JSON con el formato genérico.',
    columns: [
      { source: 'COD_TELEFONO_MOVIL', target: 'phone', required: true, note: 'normalizado a 34…' },
      { source: 'NOMBRE', target: 'name', note: 'primer token' },
      { source: 'COD_DOCUMENTO', target: 'dniEnd' },
      { source: 'LETRA_DOCUMENTO', target: 'dniLetter' },
      { source: 'COD_NUM_CUENTA', target: 'contractNumber' },
      { source: 'COD_GESTOR', target: 'agencyName' },
      { source: 'COD_SUBAGRUPACION', target: 'codSubagrupacion' },
      { source: 'FECHA_ENT', target: 'fechaEnt' },
      { source: 'REG_TRA_7D', target: 'regTra7D', note: 'solo SEGUIMIENTO; 1 = cerrar flujo' },
      { source: 'DIA_REG_TRA', target: 'diaRegTra', note: 'solo SEGUIMIENTO' }
    ]
  },

  // Preventivo CaixaBank: CSV `Nº,ID_CAMPANIA,TELEFONO1,NOMBRE_PERSONA,IDIOMA,CONTRATO`.
  caixaPreventivo: {
    id: 'caixaPreventivo',
    label: 'CaixaBank Preventivo',
    excel: false,
    accepts: ['csv', 'json'],
    description:
      'CSV preventivo de CaixaBank (separado por comas, una fila por contacto).',
    sample: [
      'Nº,ID_CAMPANIA,TELEFONO1,NOMBRE_PERSONA,IDIOMA,CONTRATO',
      '1,PREV_VTO_20261020,600000000,MARIA JOSE,ESP,30MAT0100000000000',
      '1,PREV_VTO_20261020,611111111,JORDI,CAT,2001   00000000000'
    ],
    columns: [
      { source: 'TELEFONO1', target: 'phone', required: true, note: '9 dígitos → 34…' },
      { source: 'NOMBRE_PERSONA', target: 'name' },
      { source: 'CONTRATO', target: 'contractNumber' },
      { source: 'IDIOMA', target: 'lang', note: 'ESP → es, CAT → ca… (vacío = es)' },
      { source: 'ID_CAMPANIA', target: 'campaignRef' },
      { source: 'Nº', target: null, note: 'ignorada' }
    ]
  }
}

// Mapper guardado en la template (string JSON en DynamoDB). null si no hay o es inválido.
export const parseTemplateMapper = (template) => {
  const raw = template && template.uploadMapper
  if (!raw) return null
  try {
    const m = typeof raw === 'string' ? JSON.parse(raw) : raw
    return m && Array.isArray(m.columns) && m.columns.length ? m : null
  } catch (e) {
    return null
  }
}

export const getFormatter = (template) => {
  const base = getCodeFormatter(template)
  const custom = parseTemplateMapper(template)
  if (!custom || (base && base.excel)) return base
  return {
    id: base ? base.id : 'custom',
    label: custom.label || (base ? base.label : 'Mapper de la template'),
    excel: false,
    accepts: ['csv', 'json'],
    description:
      custom.description ||
      (base ? base.description : 'CSV/JSON con las columnas indicadas.'),
    columns: custom.columns,
    fromTemplate: true
  }
}

// Formatter definido en código (sin aplicar el uploadMapper de la template).
export const getCodeFormatter = (template) => {
  if (!template) return null
  if (template.formatter && FORMATTERS[template.formatter]) {
    return FORMATTERS[template.formatter]
  }
  const flowName =
    (template.initElement || '').split('-init-')[0] || template.name || ''
  const hay = `${template.name || ''} ${flowName}`
  if (/preventivo/i.test(hay)) return FORMATTERS.caixaPreventivo
  if (/recobros|caixa|testalf/i.test(flowName)) return FORMATTERS.caixaRecobros
  return null
}

// Aplica las `columns` del formatter a las filas parseadas del fichero.
// Devuelve { rows, missing } donde `missing` son las columnas obligatorias que
// no aparecen en la cabecera. Las columnas desconocidas se descartan.
export const applyColumnMapper = (formatter, rawRows) => {
  const rows = (rawRows || []).filter((r) =>
    Object.values(r || {}).some((v) => String(v ?? '').trim() !== '')
  )
  const headers = rows.length ? Object.keys(rows[0]) : []
  const headerBy = {}
  headers.forEach((h) => {
    headerBy[normalizeHeader(h)] = h
  })
  const missing = formatter.columns
    .filter((c) => c.required && !headerBy[normalizeHeader(c.source)])
    .map((c) => c.source)

  const mapped = rows.map((el) => {
    const o = {}
    formatter.columns.forEach(({ source, target }) => {
      if (!target) return
      const h = headerBy[normalizeHeader(source)]
      if (h === undefined) return
      const val = el[h]
      if (val === undefined || val === null) return
      o[target] = String(val).trim()
    })
    return o
  })
  return { rows: mapped, missing }
}
